// HRMS-DEV/src/pages/finance/payroll/PayrollSection.jsx
// Payroll & Compensation.
//
// Full Payslip Lifecycle Enforced:
// GENERATE → EDIT → FINALIZE → PAY → SHARE → EMPLOYEE VIEW/DOWNLOAD
//
// Incorporates Half-Day Attendance & LOP Deduction:
// - Evaluates employee attendance for the selected payroll period
// - Cross-references with approved leaves (no deduction if covered by paid leave)
// - Calculates 50% per-day salary deduction per applicable unpaid half-day
// - Adds to Attendance/LOP Deduction in Total Deductions & Net Pay
// - Preserves halfDayCount, perDaySalary, halfDayDeduction, and attendanceLopDeduction

import React, { useState, useEffect, useMemo } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../../firebase/config";
import { useCollection, useAllUsers } from "../../../hooks/useFirestore";
import { useAuth } from "../../../context/AuthContext";
import Payslip from "./Payslip";
import { generatePayslipPDF } from "../../../utils/payrollPdfExport";
import { calculateEmployeeAttendanceAndLop } from "../../../utils/attendanceCalculation";
import { getPayrollSettings, recalculateAllGeneratedPayrolls } from "../../../services/payrollService";
import * as XLSX from "xlsx";

/* ════════════════════════════════════════════════════════════════
   HOOKS + ACTIONS
   ════════════════════════════════════════════════════════════════ */

function useEmployees() {
  const { data: users, loading } = useAllUsers();
  return { employees: users.filter((u) => u.isActive !== false), loading };
}

function useSalaryStructures() {
  const { data, loading } = useCollection("salaryStructures");
  return { structures: data, loading };
}

function usePendingPayments() {
  const { data, loading } = useCollection("pendingPayments");
  return { pendingPayments: data, loading };
}

function useEmployeePayslips() {
  const { data, loading } = useCollection("employeePayslips");
  return { employeePayslips: data, loading };
}

/* ════════════════════════════════════════════════════════════════
   ICONS (SVG line icons matching sidebar navigation theme)
   ════════════════════════════════════════════════════════════════ */

function SyncIcon({ size = 16, style = {} }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      width={size}
      height={size}
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );
}

function EyeIcon({ size = 15, style = {} }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      width={size}
      height={size}
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  );
}

function PdfIcon({ size = 15, style = {} }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      width={size}
      height={size}
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
      />
    </svg>
  );
}

function EditIcon({ size = 14, style = {} }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      width={size}
      height={size}
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"
      />
    </svg>
  );
}

function CheckIcon({ size = 13, style = {} }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2.2}
      stroke="currentColor"
      width={size}
      height={size}
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
    </svg>
  );
}

function DownloadIcon({ size = 15, style = {} }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.8}
      stroke="currentColor"
      width={size}
      height={size}
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0, ...style }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3"
      />
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════════
   STATUS → PILL COLOR & HELPERS
   ════════════════════════════════════════════════════════════════ */

const STATUS_COLOR = {
  Generated: "gray",
  "Pending Payment": "gray",
  "Under Review": "yellow",
  Finalized: "purple",
  Paid: "blue",
  Shared: "green",
};

function statusColor(status) {
  return STATUS_COLOR[status] || "gray";
}

function StatusPill({ status }) {
  const norm = status === "Pending Payment" ? "Generated" : status;
  return <span className={`status-pill status-pill--${statusColor(norm)}`}>{norm}</span>;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function isInternEmployee(emp) {
  if (!emp) return false;
  const label = (emp.category || emp.role || emp.designation || emp.employeeType || "")
    .toString()
    .toLowerCase();
  return label.includes("intern");
}

function getEmployeeById(employees, employeeId) {
  return employees.find((e) => e.uid === employeeId || e.id === employeeId);
}

function employeeDisplayName(emp) {
  if (!emp) return "Unnamed employee";
  return emp.displayName || emp.name || emp.email || emp.uid || "Unnamed employee";
}

function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function latestStructuresByEmployee(structures) {
  const map = new Map();
  for (const s of structures) {
    const existing = map.get(s.employeeId);
    if (!existing || toMillis(s.createdAt) >= toMillis(existing.createdAt)) {
      map.set(s.employeeId, s);
    }
  }
  return map;
}

function summarizeNames(names, max = 5) {
  if (!names || names.length === 0) return "";
  if (names.length <= max) return names.join(", ");
  return `${names.slice(0, max).join(", ")} +${names.length - max} more`;
}

/* ════════════════════════════════════════════════════════════════
   KPI CARDS
   ════════════════════════════════════════════════════════════════ */

function KpiCard({ label, value, sub, icon, color, onClick }) {
  return (
    <div
      className={`kpi-card kpi-card--${color}`}
      onClick={onClick}
      style={{
        cursor: "pointer",
        transition: "0.2s ease",
        display: "flex",
        alignItems: "center",
        gap: "0.9rem",
        height: "100%",
        minHeight: "110px",
        padding: "1.1rem 1.25rem",
        boxSizing: "border-box",
      }}
    >
      <div className="kpi-icon" style={{ flexShrink: 0 }}>{icon}</div>
      <div
        className="kpi-body"
        style={{ display: "flex", flexDirection: "column", minWidth: 0, gap: "0.15rem" }}
      >
        <span
          className="kpi-value"
          style={{ fontSize: "1.35rem", fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap" }}
        >
          {value}
        </span>
        <span
          className="kpi-label"
          style={{ fontSize: "0.85rem", color: "#374151", fontWeight: 600 }}
        >
          {label}
        </span>
        <span className="kpi-sub" style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
          {sub}
        </span>
      </div>
    </div>
  );
}

function kpiIcon(pathD) {
  return (
    <div
      style={{
        width: "45px",
        height: "45px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#1B4B31",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={1.8}
        stroke="currentColor"
        style={{ width: "30px", height: "30px" }}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={pathD} />
      </svg>
    </div>
  );
}

const ICONS = {
  cost: "M12 6v12m-3.75-9.75h5.25a2.25 2.25 0 0 1 0 4.5h-3a2.25 2.25 0 0 0 0 4.5h5.25M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  paid: "m4.5 12.75 6 6 9-13.5",
  intern:
    "M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5",
  pending: "M12 6v6l4 2m6-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z",
  pf: "M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z",
  generated:
    "M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z",
};

/* ════════════════════════════════════════════════════════════════
   MAIN PAYROLL SECTION
   ════════════════════════════════════════════════════════════════ */

export default function PayrollSection() {
  const { userProfile } = useAuth();
  const { employees } = useEmployees();
  const { structures } = useSalaryStructures();
  const { pendingPayments, loading: pendingLoading } = usePendingPayments();
  const { employeePayslips } = useEmployeePayslips();
  const { data: attendanceRecords } = useCollection("attendance");
  const { data: leaveRequests } = useCollection("leaveRequests");
  const [payrollSettings, setPayrollSettings] = useState(null);

  useEffect(() => {
    async function loadSettings() {
      const s = await getPayrollSettings();
      if (s) setPayrollSettings(s);
    }
    loadSettings();
  }, []);

  /* ── Month/Year scope ────────────────────────────────────────────── */
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const selectedMonthName = MONTHS[selectedMonth - 1];

  const scopedPendingPayments = useMemo(
    () =>
      pendingPayments.filter(
        (p) => p.month === selectedMonthName && Number(p.year) === Number(selectedYear)
      ),
    [pendingPayments, selectedMonthName, selectedYear]
  );

  /* ── Dashboard stats ─────────────────────────────────────────────── */
  const totalCost = useMemo(
    () => scopedPendingPayments.reduce((acc, p) => acc + Number(p.gross || 0), 0),
    [scopedPendingPayments]
  );

  const paidOrSharedPayments = useMemo(
    () => scopedPendingPayments.filter((p) => p.status === "Paid" || p.status === "Shared"),
    [scopedPendingPayments]
  );

  const employeesPaidList = useMemo(
    () => paidOrSharedPayments.filter((p) => !isInternEmployee(getEmployeeById(employees, p.employeeId))),
    [paidOrSharedPayments, employees]
  );

  const internsPaidList = useMemo(
    () => paidOrSharedPayments.filter((p) => isInternEmployee(getEmployeeById(employees, p.employeeId))),
    [paidOrSharedPayments, employees]
  );

  const pendingPayoutsList = useMemo(
    () => scopedPendingPayments.filter((p) => p.status === "Finalized"),
    [scopedPendingPayments]
  );

  const totalPF = useMemo(
    () => scopedPendingPayments.reduce((acc, p) => acc + Number(p.pf || 0), 0),
    [scopedPendingPayments]
  );

  /* ── KPI popup state ─────────────────────────────────────────────── */
  const [popupTitle, setPopupTitle] = useState("");
  const [popupRows, setPopupRows] = useState([]);
  const [popupValueLabel, setPopupValueLabel] = useState("Net Pay");
  const [showPopup, setShowPopup] = useState(false);

  function openKpiPopup(title, rows, valueLabel = "Net Pay") {
    setPopupTitle(title);
    setPopupRows(rows);
    setPopupValueLabel(valueLabel);
    setShowPopup(true);
  }

  const [msg, setMsg] = useState("");

  const [workedDaysByRow, setWorkedDaysByRow] = useState({});
  const [processingId, setProcessingId] = useState(null);

  // Modal states
  const [payslipFor, setPayslipFor] = useState(null);
  const [isModalEditable, setIsModalEditable] = useState(false);

  // Status filter for the Table: "all" | "Generated" | "Finalized" | "Paid" | "Shared"
  const [statusFilter, setStatusFilter] = useState("all");

  // Selection state
  const [selectedPaymentIds, setSelectedPaymentIds] = useState(new Set());
  const [bulkActionRunning, setBulkActionRunning] = useState(false);
  const [bulkShareResult, setBulkShareResult] = useState(null);

  // Bulk Generate state
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  React.useEffect(() => {
    setSelectedPaymentIds(new Set());
  }, [statusFilter]);

  // Normalize status for backward compatibility
  const getNormalizedStatus = (p) => {
    if (p.status === "Pending Payment") return "Generated";
    return p.status || "Generated";
  };

  /* ── Core Workflow Handlers ──────────────────────────────────────── */

  // Save changes to a generated/draft payslip
  const handleSavePayslip = async (updatedData) => {
    if (!payslipFor) return;
    try {
      const docRef = doc(db, "pendingPayments", payslipFor.id);
      await updateDoc(docRef, {
        ...updatedData,
        updatedAt: serverTimestamp(),
      });
      setMsg("✅ Payslip updated successfully.");
      setPayslipFor((prev) => (prev ? { ...prev, ...updatedData } : null));
    } catch (err) {
      setMsg("❌ " + err.message);
      throw err;
    }
  };

  // Finalize payslip (Locking values for payment)
  const handleFinalizePayslip = async (paymentId, payload = null) => {
    setProcessingId(paymentId);
    setMsg("");
    try {
      const docRef = doc(db, "pendingPayments", paymentId);
      const updateData = {
        ...(payload || {}),
        status: "Finalized",
        payslipStatus: "Finalized",
        finalizedAt: serverTimestamp(),
        finalizedBy: userProfile?.displayName || userProfile?.name || "Finance Admin",
      };
      await updateDoc(docRef, updateData);
      setMsg("✅ Payslip finalized successfully. It is now ready for payment processing.");
      if (payslipFor && payslipFor.id === paymentId) {
        setPayslipFor(null);
      }
    } catch (err) {
      setMsg("❌ " + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  // Mark as Paid (Only allowed if Finalized)
  const handlePaySalary = async (paymentId) => {
    const target = pendingPayments.find((p) => p.id === paymentId);
    if (!target) return;

    if (getNormalizedStatus(target) !== "Finalized") {
      alert("Validation Error: Payment can only be processed after the payslip has been Finalized.");
      return;
    }

    setProcessingId(paymentId);
    setMsg("");
    try {
      const workedDays = workedDaysByRow[paymentId] ?? target.workedDays ?? 30;
      await updateDoc(doc(db, "pendingPayments", paymentId), {
        status: "Paid",
        paymentStatus: "Paid",
        workedDays: workedDays === "" ? null : Number(workedDays),
        paidAt: serverTimestamp(),
        paidBy: userProfile?.displayName || userProfile?.name || "Finance Admin",
      });
      setMsg("✅ Payment confirmed and marked as Paid.");
    } catch (err) {
      setMsg("❌ " + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  // Share Payslip (Only allowed if Paid & Finalized)
  const handleShareSinglePayslip = async (payment) => {
    if (getNormalizedStatus(payment) !== "Paid") {
      alert("Validation Error: Only Paid payslips can be shared with employees.");
      return;
    }

    // Check duplicate
    const alreadyShared = employeePayslips.some((s) => s.paymentId === payment.id);
    if (alreadyShared) {
      alert("This payslip has already been shared with the employee.");
      return;
    }

    setProcessingId(payment.id);
    setMsg("");
    try {
      const batch = writeBatch(db);
      const ref = doc(collection(db, "employeePayslips"));

      const empRecord = getEmployeeById(employees, payment.employeeId);
      const payload = {
        employeeId: payment.employeeId,
        customEmployeeId: payment.customEmployeeId || empRecord?.employeeId || payment.employeeId,
        employeeName: payment.employeeName || empRecord?.displayName || empRecord?.name || null,
        designation: payment.designation || empRecord?.designation || empRecord?.role || "Staff",
        department: payment.department || empRecord?.department || "Engineering",
        joiningDate: payment.joiningDate || empRecord?.joiningDate || empRecord?.dateOfJoining || null,
        bankAccountNo: payment.bankAccountNo || empRecord?.accountNumber || empRecord?.bankAccountNo || "NA",
        pfNumber: payment.pfNumber || empRecord?.pfNumber || empRecord?.uan || "NA",
        paymentId: payment.id,
        month: payment.month,
        year: payment.year,
        basic: payment.basic ?? 0,
        hra: payment.hra ?? 0,
        specialAllowance: payment.specialAllowance ?? 0,
        travelAllowance: payment.travelAllowance ?? 0,
        bonusIncentive: payment.bonusIncentive ?? 0,
        otherAllowance: payment.otherAllowance ?? 0,
        pf: payment.pf ?? 0,
        professionalTax: payment.professionalTax ?? 0,
        incomeTax: payment.incomeTax ?? 0,
        esi: payment.esi ?? 0,
        absentDays: payment.absentDays ?? 0,
        halfDayCount: payment.halfDayCount ?? 0,
        perDaySalary: payment.perDaySalary ?? 0,
        fullDayLopDeduction: payment.fullDayLopDeduction ?? 0,
        halfDayDeduction: payment.halfDayDeduction ?? 0,
        attendanceLopDeduction: payment.attendanceLopDeduction ?? payment.halfDayDeduction ?? 0,
        otherDeductions: payment.otherDeductions ?? 0,
        gross: payment.gross ?? 0,
        deductions: payment.deductions ?? 0,
        net: payment.net ?? 0,
        workedDays: payment.workedDays ?? 30,
        paidAt: payment.paidAt || serverTimestamp(),
        sharedAt: serverTimestamp(),
        sharedBy: userProfile?.displayName || "Finance Admin",
      };

      batch.set(ref, payload);
      batch.update(doc(db, "pendingPayments", payment.id), {
        status: "Shared",
        sharedAt: serverTimestamp(),
      });

      await batch.commit();
      setMsg(`✅ Payslip shared with ${payment.employeeName || "employee"}.`);
    } catch (err) {
      setMsg("❌ " + err.message);
    } finally {
      setProcessingId(null);
    }
  };

  // Rows currently visible under the active status filter
  const filteredPayments = useMemo(() => {
    if (statusFilter === "all") return scopedPendingPayments;
    return scopedPendingPayments.filter((p) => getNormalizedStatus(p) === statusFilter);
  }, [scopedPendingPayments, statusFilter]);

  // Specific subsets for bulk actions
  const selectedGeneratedRows = useMemo(
    () => scopedPendingPayments.filter((p) => selectedPaymentIds.has(p.id) && getNormalizedStatus(p) === "Generated"),
    [scopedPendingPayments, selectedPaymentIds]
  );

  const selectedFinalizedRows = useMemo(
    () => scopedPendingPayments.filter((p) => selectedPaymentIds.has(p.id) && getNormalizedStatus(p) === "Finalized"),
    [scopedPendingPayments, selectedPaymentIds]
  );

  const selectedPaidRows = useMemo(
    () => scopedPendingPayments.filter((p) => selectedPaymentIds.has(p.id) && getNormalizedStatus(p) === "Paid"),
    [scopedPendingPayments, selectedPaymentIds]
  );

  function toggleSelectPayment(paymentId, checked) {
    setSelectedPaymentIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(paymentId);
      else next.delete(paymentId);
      return next;
    });
  }

  function toggleSelectAllVisible(checked) {
    setSelectedPaymentIds(checked ? new Set(filteredPayments.map((p) => p.id)) : new Set());
  }

  /* ── Bulk Actions ────────────────────────────────────────────────── */

  // Bulk Finalize
  async function handleBulkFinalize() {
    if (selectedGeneratedRows.length === 0) return;
    setBulkActionRunning(true);
    setMsg("");
    try {
      const batch = writeBatch(db);
      for (const p of selectedGeneratedRows) {
        batch.update(doc(db, "pendingPayments", p.id), {
          status: "Finalized",
          payslipStatus: "Finalized",
          finalizedAt: serverTimestamp(),
          finalizedBy: userProfile?.displayName || "Finance Admin",
        });
      }
      await batch.commit();
      setMsg(`✅ Finalized ${selectedGeneratedRows.length} payslip(s).`);
      setSelectedPaymentIds(new Set());
    } catch (err) {
      setMsg("❌ " + err.message);
    } finally {
      setBulkActionRunning(false);
    }
  }

  // Bulk Pay
  async function handleBulkPaySalaries() {
    if (selectedFinalizedRows.length === 0) return;
    setBulkActionRunning(true);
    setMsg("");
    try {
      const batch = writeBatch(db);
      for (const p of selectedFinalizedRows) {
        const workedDays = workedDaysByRow[p.id] ?? p.workedDays ?? 30;
        batch.update(doc(db, "pendingPayments", p.id), {
          status: "Paid",
          paymentStatus: "Paid",
          workedDays: workedDays === "" ? null : Number(workedDays),
          paidAt: serverTimestamp(),
          paidBy: userProfile?.displayName || "Finance Admin",
        });
      }
      await batch.commit();
      setMsg(`✅ Marked ${selectedFinalizedRows.length} employee payment(s) as Paid.`);
      setSelectedPaymentIds(new Set());
    } catch (err) {
      setMsg("❌ " + err.message);
    } finally {
      setBulkActionRunning(false);
    }
  }

  // Bulk Share
  async function handleBulkSharePayslips() {
    if (selectedPaidRows.length === 0) return;

    const alreadySharedPaymentIds = new Set(employeePayslips.map((s) => s.paymentId));
    const toShare = selectedPaidRows.filter((p) => !alreadySharedPaymentIds.has(p.id));
    const skippedAlreadyShared = selectedPaidRows
      .filter((p) => alreadySharedPaymentIds.has(p.id))
      .map((p) => p.employeeName || p.employeeId);

    setBulkActionRunning(true);
    setBulkShareResult(null);
    setMsg("");
    try {
      const CHUNK_SIZE = 450;
      for (let i = 0; i < toShare.length; i += CHUNK_SIZE) {
        const chunk = toShare.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        for (const p of chunk) {
          const ref = doc(collection(db, "employeePayslips"));
          const empRecord = getEmployeeById(employees, p.employeeId);
          batch.set(ref, {
            employeeId: p.employeeId,
            customEmployeeId: p.customEmployeeId || empRecord?.employeeId || p.employeeId,
            employeeName: p.employeeName || empRecord?.displayName || empRecord?.name || null,
            designation: p.designation || empRecord?.designation || empRecord?.role || "Staff",
            department: p.department || empRecord?.department || "Engineering",
            joiningDate: p.joiningDate || empRecord?.joiningDate || empRecord?.dateOfJoining || null,
            bankAccountNo: p.bankAccountNo || empRecord?.accountNumber || empRecord?.bankAccountNo || "NA",
            pfNumber: p.pfNumber || empRecord?.pfNumber || empRecord?.uan || "NA",
            paymentId: p.id,
            month: p.month,
            year: p.year,
            basic: p.basic ?? 0,
            hra: p.hra ?? 0,
            specialAllowance: p.specialAllowance ?? 0,
            travelAllowance: p.travelAllowance ?? 0,
            bonusIncentive: p.bonusIncentive ?? 0,
            otherAllowance: p.otherAllowance ?? 0,
            pf: p.pf ?? 0,
            professionalTax: p.professionalTax ?? 0,
            incomeTax: p.incomeTax ?? 0,
            esi: p.esi ?? 0,
            absentDays: p.absentDays ?? 0,
            halfDayCount: p.halfDayCount ?? 0,
            perDaySalary: p.perDaySalary ?? 0,
            fullDayLopDeduction: p.fullDayLopDeduction ?? 0,
            halfDayDeduction: p.halfDayDeduction ?? 0,
            attendanceLopDeduction: p.attendanceLopDeduction ?? p.halfDayDeduction ?? 0,
            otherDeductions: p.otherDeductions ?? 0,
            gross: p.gross ?? 0,
            deductions: p.deductions ?? 0,
            net: p.net ?? 0,
            workedDays: p.workedDays ?? 30,
            paidAt: p.paidAt || serverTimestamp(),
            sharedAt: serverTimestamp(),
            sharedBy: userProfile?.displayName || "Finance Admin",
          });
          batch.update(doc(db, "pendingPayments", p.id), {
            status: "Shared",
            sharedAt: serverTimestamp(),
          });
        }
        await batch.commit();
      }

      setBulkShareResult({ shared: toShare.length, skippedAlreadyShared });

      if (toShare.length > 0) {
        setMsg(`✅ Shared ${toShare.length} payslip(s) with employees.`);
      } else {
        setMsg("ℹ️ The selected payslip(s) were already shared.");
      }
      setSelectedPaymentIds(new Set());
    } catch (err) {
      setMsg("❌ " + err.message);
    } finally {
      setBulkActionRunning(false);
    }
  }

  /* ── Sync Generated Payrolls with Latest Settings ───────────────── */
  async function handleSyncSettings() {
    setBulkGenerating(true);
    setBulkResult(null);
    setMsg("");
    try {
      const currentSettings = await getPayrollSettings();
      if (currentSettings) setPayrollSettings(currentSettings);
      const res = await recalculateAllGeneratedPayrolls(currentSettings, selectedMonthName, selectedYear);
      if (res?.success) {
        setMsg(`✅ Successfully synced and updated ${res.updatedCount || 0} payroll record(s) for ${selectedMonthName} ${selectedYear} with latest settings.`);
      } else {
        setMsg("❌ Error syncing payrolls: " + (res?.error || "Unknown error"));
      }
    } catch (err) {
      setMsg("❌ " + err.message);
    } finally {
      setBulkGenerating(false);
    }
  }

  /* ── Bulk Generate & Recalculate (Auto-populates from Payroll Settings & Attendance) ─ */
  async function handleGenerateAll() {
    setBulkGenerating(true);
    setBulkResult(null);
    setMsg("");

    try {
      const currentSettings = (await getPayrollSettings()) || payrollSettings || {};
      if (currentSettings) setPayrollSettings(currentSettings);
      const latestByEmployee = latestStructuresByEmployee(structures);

      // Fetch fresh bank details from subcollections in parallel
      const bankDetailsSnaps = await Promise.all(
        employees.map(async (emp) => {
          const uid = emp.uid || emp.id;
          try {
            const bSnap = await getDoc(doc(db, "users", uid, "bank_details", "main"));
            return { uid, data: bSnap.exists() ? bSnap.data() : null };
          } catch {
            return { uid, data: null };
          }
        })
      );
      const bankMap = new Map(bankDetailsSnaps.map((b) => [b.uid, b.data]));

      // Fallback to fresh Firestore queries for attendance and leaves if local state is empty
      let attList = attendanceRecords || [];
      let leaveList = leaveRequests || [];
      if (attList.length === 0) {
        try {
          const aSnap = await getDocs(collection(db, "attendance"));
          attList = aSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (e) {
          console.warn("Could not fetch fresh attendance", e);
        }
      }
      if (leaveList.length === 0) {
        try {
          const lSnap = await getDocs(collection(db, "leaveRequests"));
          leaveList = lSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (e) {
          console.warn("Could not fetch fresh leaves", e);
        }
      }

      const toCreate = [];
      const toUpdate = [];
      const skippedLocked = [];
      const generatedWithoutStructure = [];

      for (const emp of employees) {
        const employeeId = emp.uid || emp.id;
        const structure = latestByEmployee.get(employeeId) || (emp.employeeId ? latestByEmployee.get(emp.employeeId) : null);
        const displayName = structure?.employeeName || employeeDisplayName(emp);

        const existingRecord = scopedPendingPayments.find(
          (p) => p.employeeId === employeeId || p.employeeId === emp.id || (emp.employeeId && p.employeeId === emp.employeeId)
        );

        // If locked (Paid or Shared), skip modifying it
        if (existingRecord && (existingRecord.status === "Paid" || existingRecord.status === "Shared")) {
          skippedLocked.push(displayName);
          continue;
        }

        let basic = 0;
        let hra = 0;
        let specialAllowance = 0;
        let travelAllowance = 0;
        let bonusIncentive = 0;
        let otherAllowance = 0;
        let pf = 0;
        let esi = 0;
        let professionalTax = 0;
        let incomeTax = 0;
        let otherDeductions = 0;

        const empCategory = (emp.payrollCategory || emp.employmentType || emp.category || "full-time").toString().toLowerCase();
        const isUnpaid = empCategory === "unpaid-intern" || empCategory === "unpaid";
        const isDeferred = empCategory === "deferred-intern" || empCategory === "deferred";

        if (structure) {
          basic = Number(structure.basic || 0);
          hra = Number(structure.hra || 0);
          specialAllowance = Number(structure.specialAllowance || 0);
          travelAllowance = Number(structure.travelAllowance || 0);
          bonusIncentive = Number(structure.bonusIncentive ?? currentSettings.bonus ?? 0);
          otherAllowance = Number(structure.otherAllowance || 0);
          esi = Number(structure.esi || 0);
          professionalTax = Number(structure.professionalTax || 0);
          incomeTax = Number(structure.incomeTax || 0);
          otherDeductions = Number(structure.otherDeductions || 0);

          if (isUnpaid || isDeferred || empCategory.includes("intern")) {
            pf = 0;
          } else {
            pf = Math.round((basic * Number(currentSettings.pfEmployee || 12)) / 100);
          }
        } else {
          generatedWithoutStructure.push(displayName);
          // Auto-resolve salary configuration based on employee profile & Payroll Settings
          if (empCategory === "paid-intern" || empCategory === "fresher-intern") {
            basic = Number(emp.stipend || emp.salary || currentSettings.fresherStipend || 8000);
            pf = 0;
          } else if (empCategory === "paid-intern-experienced" || empCategory === "experienced-intern") {
            basic = Number(emp.stipend || emp.salary || currentSettings.experiencedStipend || 15000);
            pf = 0;
          } else if (isUnpaid) {
            basic = 0;
            pf = 0;
          } else if (isDeferred) {
            basic = Number(emp.stipend || emp.salary || currentSettings.deferredStipend || 8000);
            pf = 0;
          } else {
            // Full-time employee
            basic = Number(emp.fullTimeSalary || emp.baseSalary || emp.salary || currentSettings.fullTimeSalary || 30000);
            const pfRate = Number(currentSettings.pfEmployee || 12);
            pf = Math.round((basic * pfRate) / 100);
          }
          bonusIncentive = Number(emp.bonus || currentSettings.bonus || 0);
        }

        const rowGross = basic + hra + specialAllowance + travelAllowance + bonusIncentive + otherAllowance;
        const salaryDays = 30;

        // Comprehensive Attendance & LOP Deductions
        const attInfo = calculateEmployeeAttendanceAndLop({
          employeeId,
          candidateIds: [emp.uid, emp.id, emp.employeeId, emp.customId, emp.empId, employeeId],
          month: selectedMonth,
          year: selectedYear,
          monthlyGross: rowGross,
          salaryDays,
          attendanceList: attList,
          leavesList: leaveList,
        });

        const presentDays = attInfo.presentDays;
        const leaveDays = attInfo.paidLeaveDays;
        const absentDays = attInfo.absentDays;
        const halfDayCount = attInfo.halfDayCount;
        const perDaySalary = attInfo.perDaySalary;
        const fullDayLopDeduction = attInfo.fullDayLopDeduction;
        const halfDayDeduction = attInfo.halfDayDeduction;
        const attendanceLopDeduction = attInfo.totalAttendanceLopDeduction;
        const workedDays = attInfo.workedDays ?? (presentDays + leaveDays);

        const rowDeductions = pf + esi + professionalTax + incomeTax + otherDeductions + attendanceLopDeduction;
        const rowNet = isUnpaid || isDeferred ? 0 : Math.max(0, rowGross - rowDeductions);

        const empName = emp.displayName || emp.name || displayName;
        const customEmployeeId = emp.employeeId || emp.customId || emp.empId || emp.uid || employeeId;
        const designation = emp.designation || emp.role || "Staff";
        const department = emp.department || emp.dept || "Engineering";
        const joiningDate = emp.joiningDate || emp.dateOfJoining || emp.internshipStartDate || null;

        const bankData = bankMap.get(employeeId) || bankMap.get(emp.uid) || bankMap.get(emp.id) || {};
        const bankAccountNo = bankData.accountNumber || bankData.bankAccountNo || emp.accountNumber || emp.bankAccountNo || emp.bankAccountNumber || "NA";
        const bankName = bankData.bankName || emp.bankName || "NA";
        const ifscCode = bankData.ifscCode || emp.ifscCode || "NA";
        const pfNumber = bankData.pfNumber || bankData.uan || emp.pfNumber || emp.pfNo || emp.uan || "NA";

        const payloadData = {
          employeeId,
          customEmployeeId,
          employeeName: empName,
          designation,
          department,
          joiningDate,
          bankAccountNo,
          bankName,
          ifscCode,
          pfNumber,
          month: selectedMonthName,
          year: selectedYear,
          basic,
          hra,
          specialAllowance,
          travelAllowance,
          bonusIncentive,
          otherAllowance,
          pf,
          esi,
          professionalTax,
          incomeTax,
          absentDays,
          halfDayCount,
          perDaySalary,
          fullDayLopDeduction,
          halfDayDeduction,
          attendanceLopDeduction,
          otherDeductions,
          gross: rowGross,
          deductions: rowDeductions,
          net: rowNet,
          presentDays,
          leaveDays,
          workedDays: existingRecord?.workedDays ?? workedDays,
        };

        if (existingRecord) {
          toUpdate.push({
            id: existingRecord.id,
            name: displayName,
            data: {
              ...payloadData,
              updatedAt: serverTimestamp(),
            },
          });
        } else {
          toCreate.push({
            ...payloadData,
            status: isDeferred ? "Deferred" : "Generated",
            payslipStatus: isDeferred ? "Deferred" : "Generated",
            paymentStatus: "Pending",
            createdAt: serverTimestamp(),
            createdBy: userProfile?.displayName || "Finance Admin",
          });
        }
      }

      const CHUNK_SIZE = 450;
      for (let i = 0; i < toCreate.length; i += CHUNK_SIZE) {
        const chunk = toCreate.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        for (const payment of chunk) {
          const ref = doc(collection(db, "pendingPayments"));
          batch.set(ref, payment);
        }
        await batch.commit();
      }

      for (let i = 0; i < toUpdate.length; i += CHUNK_SIZE) {
        const chunk = toUpdate.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        for (const item of chunk) {
          batch.update(doc(db, "pendingPayments", item.id), item.data);
        }
        await batch.commit();
      }

      setBulkResult({
        generated: toCreate.length + toUpdate.length,
        created: toCreate.length,
        updated: toUpdate.length,
        generatedNames: [...toCreate.map((p) => p.employeeName), ...toUpdate.map((p) => p.name)],
        skippedExisting: skippedLocked,
        generatedWithoutStructure,
      });

      const totalProcessed = toCreate.length + toUpdate.length;
      if (totalProcessed > 0) {
        setMsg(
          `✅ Successfully processed ${totalProcessed} payslip(s) for ${selectedMonthName} ${selectedYear} (${toCreate.length} created, ${toUpdate.length} recalculated & updated).`
        );
      } else {
        setMsg("ℹ️ All active employee payslips for this period are already locked (Paid / Shared).");
      }
    } catch (err) {
      setMsg("❌ " + err.message);
    } finally {
      setBulkGenerating(false);
    }
  }

  const totalActiveEmployeeCount = employees.length;

  /* ── Download Monthly Salary Payment Report (Excel) ──────────────── */
  const handleDownloadMonthlyReport = () => {
    if (!selectedMonth || !selectedYear) {
      alert("Please select a valid Month and Year before downloading the report.");
      return;
    }

    if (!scopedPendingPayments || scopedPendingPayments.length === 0) {
      alert(`No payroll records found for ${selectedMonthName} ${selectedYear} to generate a report.`);
      return;
    }

    try {
      // 1. Prepare sorted records by employee name
      const rows = scopedPendingPayments
        .map((p) => {
          const empRecord = getEmployeeById(employees, p.employeeId);
          const name = p.employeeName || empRecord?.displayName || empRecord?.name || p.employeeId || "Unnamed Employee";
          const empId = p.customEmployeeId || empRecord?.employeeId || p.employeeId || "—";
          const period = `${p.month || selectedMonthName} ${p.year || selectedYear}`;
          const gross = Number(p.gross || 0);
          const deductions = Number(p.deductions || 0);
          const net = Number(p.net || 0);
          return { name, empId, period, gross, deductions, net };
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      // 2. Calculate dynamic sum of Net Pay
      const totalNetPay = rows.reduce((sum, r) => sum + r.net, 0);

      // 3. Construct Array-of-Arrays data with exact requested column order
      const aoaData = [
        ["Employee Name", "Employee ID", "Period", "Gross Pay", "Deductions", "Net Pay"],
        ...rows.map((r) => [
          r.name,
          r.empId,
          r.period,
          r.gross,
          r.deductions,
          r.net,
        ]),
        ["", "", "", "", "", ""],
        ["", "", "", "", "Total Net Pay", totalNetPay],
      ];

      // 4. Create worksheet and workbook
      const ws = XLSX.utils.aoa_to_sheet(aoaData);

      // Column widths for professional layout
      ws["!cols"] = [
        { wch: 28 }, // Employee Name
        { wch: 18 }, // Employee ID
        { wch: 18 }, // Period
        { wch: 16 }, // Gross Pay
        { wch: 16 }, // Deductions
        { wch: 18 }, // Net Pay
      ];

      const wb = XLSX.utils.book_new();
      const sheetName = `${selectedMonthName} ${selectedYear}`.slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);

      // 5. Trigger download
      const fileName = `Salary_Payment_Report_${selectedMonthName}_${selectedYear}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error("Error downloading salary payment report:", err);
      alert("Failed to generate Excel report: " + err.message);
    }
  };

  return (
    <>
      {msg && (
        <div
          className={msg.startsWith("✅") ? "form-success" : msg.startsWith("ℹ️") ? "form-notice" : "form-error"}
          style={{ marginBottom: "1.5rem" }}
        >
          {msg}
        </div>
      )}

      {/* ── Header: Month/Year scope + Download Report Button ──────── */}
      <div
        className="page-header"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Payroll &amp; Compensation</h2>
          <p style={{ margin: "0.25rem 0 0", color: "var(--text-secondary, #666)" }}>
            Enforced Workflow: Generate → Edit &amp; Review → Finalize → Pay → Share
          </p>
        </div>
        <div className="table-filters" style={{ margin: 0, display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleDownloadMonthlyReport}
            style={{
              padding: "0.45rem 0.9rem",
              fontSize: "0.85rem",
              fontWeight: 600,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.45rem",
              cursor: "pointer",
              borderRadius: "8px",
              whiteSpace: "nowrap",
            }}
            title={`Download Excel Salary Payment Report for ${selectedMonthName} ${selectedYear}`}
          >
            <DownloadIcon size={15} />
            <span>Download Report</span>
          </button>
          <select
            className="filter-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            style={{ borderRadius: "8px" }}
          >
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <input
            className="filter-select"
            type="number"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            style={{ width: "90px", borderRadius: "8px" }}
          />
        </div>
      </div>

      {/* ── KPI Cards ──────────────────────────────────────────────── */}
      <div
        className="kpi-grid"
        style={{
          marginBottom: "2rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "1.25rem",
          alignItems: "stretch",
        }}
      >
        <KpiCard
          label="Total Payroll Cost"
          value={pendingLoading ? "—" : `₹${totalCost.toLocaleString()}`}
          sub={`${selectedMonthName} ${selectedYear}`}
          icon={kpiIcon(ICONS.cost)}
          color="#1B4B31"
          onClick={() =>
            openKpiPopup(
              "Total Payroll Cost",
              scopedPendingPayments.map((p) => ({ ...p, value: p.gross })),
              "Gross Pay"
            )
          }
        />
        <KpiCard
          label="Employees Paid"
          value={pendingLoading ? "—" : employeesPaidList.length}
          sub="Full-time employees"
          icon={kpiIcon(ICONS.paid)}
          color="#1B4B31"
          onClick={() =>
            openKpiPopup(
              "Employees Paid",
              employeesPaidList.map((p) => ({ ...p, value: p.net })),
              "Net Pay"
            )
          }
        />
        <KpiCard
          label="Interns Paid"
          value={pendingLoading ? "—" : internsPaidList.length}
          sub="Interns"
          icon={kpiIcon(ICONS.intern)}
          color="#1B4B31"
          onClick={() =>
            openKpiPopup(
              "Interns Paid",
              internsPaidList.map((p) => ({ ...p, value: p.net })),
              "Net Pay"
            )
          }
        />
        <KpiCard
          label="Ready for Payment"
          value={pendingLoading ? "—" : pendingPayoutsList.length}
          sub="Finalized records"
          icon={kpiIcon(ICONS.pending)}
          color="#1B4B31"
          onClick={() =>
            openKpiPopup(
              "Ready for Payment",
              pendingPayoutsList.map((p) => ({ ...p, value: p.net })),
              "Net Pay"
            )
          }
        />
        <KpiCard
          label="Total PF Contributions"
          value={pendingLoading ? "—" : `₹${totalPF.toLocaleString()}`}
          sub={`${selectedMonthName} ${selectedYear}`}
          icon={kpiIcon(ICONS.pf)}
          color="#1B4B31"
          onClick={() =>
            openKpiPopup(
              "Total PF Contributions",
              scopedPendingPayments.map((p) => ({ ...p, value: p.pf })),
              "PF"
            )
          }
        />
        <KpiCard
          label="Payslips Generated"
          value={pendingLoading ? "—" : scopedPendingPayments.length}
          sub="This period"
          icon={kpiIcon(ICONS.generated)}
          color="#1B4B31"
          onClick={() =>
            openKpiPopup(
              "Payslips Generated",
              scopedPendingPayments.map((p) => ({ ...p, value: p.net })),
              "Net Pay"
            )
          }
        />
      </div>

      {/* ── Step 1: Bulk Generation Card ─────────────────────────────── */}
      <div
        className="card"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1.5rem",
          padding: "1.5rem 1.75rem",
          marginBottom: "2rem",
          background: "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)",
          border: "1px solid #d1fae5",
          borderRadius: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem", flex: "1 1 320px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "44px",
              height: "44px",
              minWidth: "44px",
              borderRadius: "10px",
              background: "#16a34a",
              color: "#fff",
              fontSize: "1.3rem",
            }}
          >
            ⚡
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: "1.15rem" }}>
              1. Generate Payslips — {selectedMonthName} {selectedYear}
            </h2>
            <p style={{ margin: "0.35rem 0 0", color: "#555", fontSize: "0.9rem", maxWidth: "580px" }}>
              Bulk-generates initial Draft payslips for all {totalActiveEmployeeCount} employee(s) using salary structures and attendance.
              Applicable unpaid half-days are automatically calculated as a 50% per-day salary deduction.
            </p>
            {bulkResult && (
              <div style={{ marginTop: "0.6rem", fontSize: "0.82rem", color: "#374151" }}>
                <div>✓ Generated: <strong>{bulkResult.generated}</strong></div>
                {bulkResult.skippedExisting.length > 0 && (
                  <div>
                    ↷ Skipped, already existing: <strong>{bulkResult.skippedExisting.length}</strong>
                    {" "}({summarizeNames(bulkResult.skippedExisting)})
                  </div>
                )}
                {bulkResult.generatedWithoutStructure.length > 0 && (
                  <div style={{ color: "#b45309" }}>
                    ⚠ No structure on file, generated at ₹0:{" "}
                    <strong>{bulkResult.generatedWithoutStructure.length}</strong>
                    {" "}({summarizeNames(bulkResult.generatedWithoutStructure)})
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button
            className="btn-secondary"
            onClick={handleSyncSettings}
            disabled={bulkGenerating || scopedPendingPayments.length === 0}
            style={{
              padding: "0.75rem 1.25rem",
              fontSize: "0.92rem",
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
            title="Recalculate all draft payslips for this month using latest Payroll Settings"
          >
            <SyncIcon size={16} />
            <span>Sync with Payroll Settings</span>
          </button>
          <button
            className="btn-primary"
            onClick={handleGenerateAll}
            disabled={bulkGenerating || totalActiveEmployeeCount === 0}
            style={{ padding: "0.75rem 1.75rem", fontSize: "0.95rem", whiteSpace: "nowrap" }}
          >
            {bulkGenerating ? "Processing…" : "Generate Payroll for All Employees"}
          </button>
        </div>
      </div>

      {/* ── Step 2 - 5: Manage Payslips & Processing Table ────────────── */}
      <div className="form-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
            marginBottom: "1rem",
          }}
        >
          <div>
            <h3 className="form-card-title" style={{ margin: 0 }}>
              Payroll Management &amp; Payslip Workflow
            </h3>
            <p style={{ margin: "0.2rem 0 0", fontSize: "0.82rem", color: "#6b7280" }}>
              Workflow: [Generated] → [Finalized] → [Paid] → [Shared]
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ fontSize: "0.85rem" }}
            >
              <option value="all">All statuses</option>
              <option value="Generated">Generated / Draft</option>
              <option value="Finalized">Finalized (Ready to Pay)</option>
              <option value="Paid">Paid (Ready to Share)</option>
              <option value="Shared">Shared with Employee</option>
            </select>

            {/* Bulk Actions Contextual to current selections */}
            {selectedGeneratedRows.length > 0 && (
              <button
                className="btn-primary"
                onClick={handleBulkFinalize}
                disabled={bulkActionRunning}
                style={{ padding: "0.5rem 1.1rem", fontSize: "0.85rem", background: "#2563eb", borderColor: "#2563eb" }}
              >
                {bulkActionRunning ? "Finalizing…" : `Finalize Selected (${selectedGeneratedRows.length})`}
              </button>
            )}

            {selectedFinalizedRows.length > 0 && (
              <button
                className="btn-primary"
                onClick={handleBulkPaySalaries}
                disabled={bulkActionRunning}
                style={{ padding: "0.5rem 1.1rem", fontSize: "0.85rem" }}
              >
                {bulkActionRunning ? "Processing…" : `Pay Selected (${selectedFinalizedRows.length})`}
              </button>
            )}

            {selectedPaidRows.length > 0 && (
              <button
                className="btn-primary"
                onClick={handleBulkSharePayslips}
                disabled={bulkActionRunning}
                style={{ padding: "0.5rem 1.1rem", fontSize: "0.85rem", background: "#16a34a", borderColor: "#16a34a" }}
              >
                {bulkActionRunning ? "Sharing…" : `Share Selected (${selectedPaidRows.length})`}
              </button>
            )}
          </div>
        </div>

        {bulkShareResult && (
          <div style={{ marginBottom: "0.75rem", fontSize: "0.82rem", color: "#374151" }}>
            <div>✓ Shared to employee logins: <strong>{bulkShareResult.shared}</strong></div>
            {bulkShareResult.skippedAlreadyShared.length > 0 && (
              <div>
                ↷ Already shared, skipped: <strong>{bulkShareResult.skippedAlreadyShared.length}</strong>
                {" "}({summarizeNames(bulkShareResult.skippedAlreadyShared)})
              </div>
            )}
          </div>
        )}

        {pendingLoading ? (
          <p>Loading payroll records…</p>
        ) : scopedPendingPayments.length === 0 ? (
          <div className="card-empty" style={{ padding: "2.5rem", textAlign: "center" }}>
            <p style={{ fontWeight: 600, fontSize: "1.05rem" }}>No payroll records for {selectedMonthName} {selectedYear}</p>
            <p style={{ color: "#6b7280", marginTop: "0.4rem" }}>
              Click <strong>"Generate Payroll for All Employees"</strong> above to initiate the workflow for this period.
            </p>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="card-empty" style={{ padding: "2rem", textAlign: "center" }}>
            <p>No payslips match the filter "{statusFilter}".</p>
          </div>
        ) : (
          <div
            className="data-table-wrap"
            style={{ maxHeight: "500px", overflowY: "auto", position: "relative" }}
          >
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: "36px" }}>
                    <input
                      type="checkbox"
                      checked={filteredPayments.length > 0 && selectedPaymentIds.size === filteredPayments.length}
                      onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                      title="Select all visible"
                    />
                  </th>
                  <th>Employee</th>
                  <th>Period</th>
                  <th>Gross Pay</th>
                  <th>Deductions</th>
                  <th>Net Pay</th>
                  <th>Worked Days</th>
                  <th>Status</th>
                  <th style={{ minWidth: "200px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((p) => {
                  const normStatus = getNormalizedStatus(p);
                  const isBusy = processingId === p.id;
                  const employeeRecord = getEmployeeById(employees, p.employeeId);
                  const halfDayCount = Number(p.halfDayCount || 0);
                  const lopAmount = Number(p.attendanceLopDeduction ?? p.halfDayDeduction ?? 0);

                  return (
                    <tr key={p.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedPaymentIds.has(p.id)}
                          onChange={(e) => toggleSelectPayment(p.id, e.target.checked)}
                        />
                      </td>
                      <td>
                        <strong>{p.employeeName || p.employeeId}</strong>
                        <div style={{ fontSize: "0.72rem", color: "#111827", fontWeight: 500 }}>
                          {p.customEmployeeId || employeeRecord?.employeeId || p.employeeId}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                          {p.department || employeeRecord?.department || "Engineering"} · {p.designation || employeeRecord?.designation || employeeRecord?.role || "Staff"}
                        </div>
                        {lopAmount > 0 ? (
                          <div style={{ fontSize: "0.72rem", color: "#b45309", fontWeight: 600, marginTop: "2px" }}>
                            ⚠️ LOP: -₹{lopAmount.toLocaleString()}
                            {Number(p.absentDays || 0) > 0 && ` (${p.absentDays} Absent @100%)`}
                            {halfDayCount > 0 && ` (${halfDayCount} Half-day${halfDayCount > 1 ? "s" : ""} @50%)`}
                          </div>
                        ) : (
                          (Number(p.absentDays || 0) > 0 || halfDayCount > 0) && (
                            <div style={{ fontSize: "0.72rem", color: "#6b7280", marginTop: "2px" }}>
                              {Number(p.absentDays || 0) > 0 && `${p.absentDays} Absent day(s)`}
                              {halfDayCount > 0 && ` · ${halfDayCount} Half-day(s)`}
                            </div>
                          )
                        )}
                      </td>
                      <td>{p.month} {p.year}</td>
                      <td>₹{Number(p.gross || 0).toLocaleString()}</td>
                      <td style={{ color: "#991b1b" }}>-₹{Number(p.deductions || 0).toLocaleString()}</td>
                      <td style={{ fontWeight: 700, color: "#166534" }}>
                        ₹{Number(p.net || 0).toLocaleString()}
                      </td>
                      <td>
                        {normStatus === "Generated" ? (
                          <input
                            type="number"
                            min={0}
                            max={31}
                            style={{ width: "65px", padding: "2px 4px", fontSize: "0.85rem" }}
                            value={workedDaysByRow[p.id] ?? p.workedDays ?? 30}
                            onChange={(e) =>
                              setWorkedDaysByRow((prev) => ({ ...prev, [p.id]: e.target.value }))
                            }
                          />
                        ) : (
                          p.workedDays ?? "NA"
                        )}
                      </td>
                      <td><StatusPill status={normStatus} /></td>
                      <td>
                        <div className="action-btns" style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>

                          {/* 1. Generated Stage Actions: View, Edit, Finalize */}
                          {normStatus === "Generated" && (
                            <>
                              <button
                                className="btn-secondary"
                                style={{
                                  padding: "0.3rem 0.65rem",
                                  fontSize: "0.78rem",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.35rem",
                                }}
                                onClick={() => {
                                  setPayslipFor(p);
                                  setIsModalEditable(true);
                                }}
                              >
                                <EditIcon size={13} />
                                <span>Edit</span>
                              </button>
                              <button
                                className="btn-primary"
                                style={{ padding: "0.3rem 0.65rem", fontSize: "0.78rem", background: "#2563eb", borderColor: "#2563eb" }}
                                disabled={isBusy}
                                onClick={() => handleFinalizePayslip(p.id)}
                              >
                                {isBusy ? "…" : "Finalize"}
                              </button>
                            </>
                          )}

                          {/* 2. Finalized Stage Actions: View, Pay */}
                          {normStatus === "Finalized" && (
                            <>
                              <button
                                className="btn-ghost-sm"
                                style={{
                                  padding: "0.3rem 0.65rem",
                                  fontSize: "0.78rem",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.35rem",
                                }}
                                onClick={() => {
                                  setPayslipFor(p);
                                  setIsModalEditable(false);
                                }}
                              >
                                <EyeIcon size={14} />
                                <span>View</span>
                              </button>
                              <button
                                className="btn-primary"
                                style={{ padding: "0.3rem 0.65rem", fontSize: "0.78rem" }}
                                disabled={isBusy}
                                onClick={() => handlePaySalary(p.id)}
                              >
                                {isBusy ? "Paying…" : "Pay Salary"}
                              </button>
                            </>
                          )}

                          {/* 3. Paid Stage Actions: View, Share */}
                          {normStatus === "Paid" && (
                            <>
                              <button
                                className="btn-ghost-sm"
                                style={{
                                  padding: "0.3rem 0.65rem",
                                  fontSize: "0.78rem",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.35rem",
                                }}
                                onClick={() => {
                                  setPayslipFor(p);
                                  setIsModalEditable(false);
                                }}
                              >
                                <EyeIcon size={14} />
                                <span>View</span>
                              </button>
                              <button
                                className="btn-primary"
                                style={{ padding: "0.3rem 0.65rem", fontSize: "0.78rem", background: "#16a34a", borderColor: "#16a34a" }}
                                disabled={isBusy}
                                onClick={() => handleShareSinglePayslip(p)}
                              >
                                {isBusy ? "Sharing…" : "Share Payslip"}
                              </button>
                            </>
                          )}

                          {/* 4. Shared Stage Actions: View, Download PDF */}
                          {normStatus === "Shared" && (
                            <>
                              <button
                                className="btn-ghost-sm"
                                style={{
                                  padding: "0.3rem 0.65rem",
                                  fontSize: "0.78rem",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.35rem",
                                }}
                                onClick={() => {
                                  setPayslipFor(p);
                                  setIsModalEditable(false);
                                }}
                              >
                                <EyeIcon size={14} />
                                <span>View</span>
                              </button>
                              <button
                                className="btn-secondary"
                                style={{
                                  padding: "0.3rem 0.65rem",
                                  fontSize: "0.78rem",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.35rem",
                                }}
                                onClick={() => generatePayslipPDF(p, employeeRecord)}
                              >
                                <PdfIcon size={14} />
                                <span>PDF</span>
                              </button>
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#16a34a",
                                  fontWeight: 600,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                }}
                              >
                                <CheckIcon size={12} />
                                <span>Shared</span>
                              </span>
                            </>
                          )}

                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── KPI Popup Modal ─────────────────────────────────────────── */}
      {showPopup && (
        <div
          onClick={() => setShowPopup(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "700px",
              maxHeight: "80vh",
              overflowY: "auto",
              background: "#fff",
              borderRadius: "16px",
              padding: "1.2rem",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "1.2rem", color: "#1B4B31" }}>
                {popupTitle}
              </h2>
              <button
                onClick={() => setShowPopup(false)}
                style={{ border: "none", background: "transparent", fontSize: "1.4rem", cursor: "pointer" }}
              >
                ×
              </button>
            </div>

            {popupRows.length === 0 ? (
              <div className="card-empty">No records found.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {popupRows.map((p) => (
                  <div
                    key={p.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "0.8rem",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      background: "#f9fafb",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                      <div
                        style={{
                          width: "42px",
                          height: "42px",
                          borderRadius: "50%",
                          background: "#1B4B31",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "700",
                        }}
                      >
                        {(p.employeeName || p.employeeId || "??").slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: "600", color: "#111827" }}>
                          {p.employeeName || p.employeeId}
                        </div>
                        <div style={{ fontSize: "0.82rem", color: "#6b7280" }}>
                          {p.month} {p.year} · <StatusPill status={p.status} />
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: "0.9rem", color: "#374151", fontWeight: "600", textAlign: "right" }}>
                      ₹{Number(p.value || 0).toLocaleString()}
                      <div style={{ fontSize: "0.72rem", color: "#9ca3af", fontWeight: 400 }}>
                        {popupValueLabel}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Payslip View / Edit Modal ────────────────────────────────── */}
      {payslipFor && (
        <Payslip
          payment={payslipFor}
          employee={employees.find((e) => e.uid === payslipFor.employeeId || e.id === payslipFor.employeeId)}
          isEditable={isModalEditable}
          onSave={handleSavePayslip}
          onFinalize={(payload) => handleFinalizePayslip(payslipFor.id, payload)}
          onClose={() => setPayslipFor(null)}
        />
      )}
    </>
  );
}